/** Manage → API: API keys, outgoing webhooks and a short guide to the REST API. */
import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { ErrorBox, Modal, Panel, fmtDate } from '../components';
import { CopyLink } from './ManageTeam';
import { GitPanel, TelegramGroupsPanel } from './ManageConnect';
import { N_, t } from '../i18n';

interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  read_only: boolean;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_by: string | null;
}

interface Hook {
  id: number;
  name: string;
  url: string;
  events: string[];
  project_ids: number[] | null;
  headers: Record<string, string>;
  payload_template: string | null;
  secret: string;
  enabled: boolean;
  last_delivery: { status: number | null; error: string | null; created_at: string } | null;
}

interface DeliveryRow {
  id: number;
  event: string;
  issue_id: number | null;
  status: number | null;
  error: string | null;
  duration_ms: number;
  request: string;
  response: string;
  created_at: string;
}

const EVENTS: [string, string][] = [
  ['issue.created', N_('Issue created')],
  ['issue.updated', N_('Issue changed (status, assignee, fields)')],
  ['note.added', N_('Note added (public notes only)')],
];

const EXAMPLE_TEMPLATE = `{
  "source": "BugTracker",
  "event": "{{event}}",
  "id": {{issue.id|json}},
  "title": "{{issue.summary}}",
  "status": "{{issue.status}}",
  "priority": "{{issue.priority}}",
  "project": "{{issue.project.name}}",
  "assignee": "{{issue.handler.username}}",
  "url": "{{issue.url}}",
  "changed_by": "{{actor.username}}",
  "note": "{{note.text}}"
}`;

export function ApiTab() {
  return (
    <>
      <KeysPanel />
      <WebhooksPanel />
      <GitPanel />
      <TelegramGroupsPanel />
      <ApiGuide />
    </>
  );
}

// ---------------------------------------------------------------------------
function KeysPanel() {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ['api-keys'], queryFn: () => api.get<ApiKey[]>('/api/manage/api-keys') });
  const [name, setName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<{ id: number; key: string }>('/api/manage/api-keys', { name: name.trim(), read_only: readOnly }),
    onSuccess: (r) => {
      setNewKey({ name: name.trim(), key: r.key });
      setName('');
      setReadOnly(false);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.del(`/api/manage/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  return (
    <Panel
      title={t('API keys')}
      actions={
        <form
          className="inline-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Key name, e.g. Ship')} maxLength={80} />
          <label className="check">
            <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} /> {t('Read only')}
          </label>
          <button className="primary" disabled={!name.trim() || create.isPending}>
            {t('Create key')}
          </button>
        </form>
      }
    >
      <ErrorBox error={error || create.error || revoke.error} />
      {newKey && (
        <div className="banner success">
          {t('Key "{name}" created. Copy it now — it is shown only once:', { name: newKey.name })} <CopyLink text={newKey.key} />
        </div>
      )}
      <p className="muted small">
        {t('Requests with a key act as the person who created it, with their rights in this team. Revoke a key when it is no longer needed.')}
      </p>
      {data && data.length > 0 && (
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Key')}</th>
              <th>{t('Access')}</th>
              <th>{t('Created by')}</th>
              <th>{t('Created')}</th>
              <th>{t('Last used')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((k) => (
              <tr key={k.id} className={k.revoked_at ? 'disabled' : ''}>
                <td>{k.name}</td>
                <td className="mono">{k.prefix}…</td>
                <td>{k.read_only ? t('read only') : t('read & write')}</td>
                <td>{k.created_by}</td>
                <td className="nowrap">{fmtDate(k.created_at, false)}</td>
                <td className="nowrap">{k.last_used_at ? fmtDate(k.last_used_at) : '—'}</td>
                <td>
                  {k.revoked_at ? (
                    <span className="pill">{t('revoked')}</span>
                  ) : (
                    <button className="link danger" onClick={() => confirm(t('Revoke the key "{name}"? Programs using it stop working.', { name: k.name })) && revoke.mutate(k.id)}>
                      {t('Revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
const headersToText = (h: Record<string, string>) =>
  Object.entries(h)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
function textToHeaders(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of s.split('\n')) {
    const i = line.indexOf(':');
    if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return out;
}

function WebhooksPanel() {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ['webhooks'], queryFn: () => api.get<Hook[]>('/api/manage/webhooks') });
  const [editing, setEditing] = useState<Partial<Hook> | null>(null);
  const [logFor, setLogFor] = useState<Hook | null>(null);
  const [sendResult, setSendResult] = useState<{ hook: string; ok: boolean; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['webhooks'] });
  const toggle = useMutation({ mutationFn: (h: Hook) => api.patch(`/api/manage/webhooks/${h.id}`, { enabled: !h.enabled }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: number) => api.del(`/api/manage/webhooks/${id}`), onSuccess: refresh });
  const send = useMutation({
    mutationFn: (h: Hook) => api.post<{ status: number | null; error: string | null; duration_ms: number }>(`/api/manage/webhooks/${h.id}/send`, {}),
    onSuccess: (d, h) => {
      setSendResult({ hook: h.name, ok: !d.error, text: d.error ? d.error : t('HTTP {status} in {ms} ms', { status: d.status, ms: d.duration_ms }) });
      refresh();
    },
  });

  return (
    <Panel
      title={t('Webhooks (send issues to another system)')}
      actions={
        <button className="primary" onClick={() => setEditing({ events: ['issue.created', 'issue.updated', 'note.added'], enabled: true, headers: {}, project_ids: null })}>
          {t('New webhook')}
        </button>
      }
    >
      <ErrorBox error={error || toggle.error || remove.error || send.error} />
      {sendResult && (
        <div className={`banner ${sendResult.ok ? 'success' : 'error'}`}>
          {t('Test sent to "{name}": {result}', { name: sendResult.hook, result: sendResult.text })}
        </div>
      )}
      <p className="muted small">
        {t('When an issue is created or changed, the server sends a POST request with JSON to the endpoint. Each request carries the header X-BugTracker-Signature: sha256=HMAC(secret, body), so the receiver can check that it came from here.')}
      </p>
      {editing && (
        <WebhookForm
          hook={editing}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {data && data.length > 0 && (
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Endpoint')}</th>
              <th>{t('Events')}</th>
              <th>{t('Last delivery')}</th>
              <th>{t('Enabled')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((h) => (
              <tr key={h.id} className={h.enabled ? '' : 'disabled'}>
                <td>{h.name}</td>
                <td className="mono small break">{h.url}</td>
                <td className="small">{h.events.join(', ')}</td>
                <td className="nowrap small">
                  {h.last_delivery ? (
                    <span className={h.last_delivery.error ? 'error' : ''} title={h.last_delivery.error ?? ''}>
                      {h.last_delivery.status ?? '—'} · {fmtDate(h.last_delivery.created_at)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <input type="checkbox" checked={h.enabled} onChange={() => toggle.mutate(h)} aria-label={t('Enabled')} />
                </td>
                <td className="nowrap">
                  <button className="link" onClick={() => setEditing(h)}>
                    {t('Edit')}
                  </button>{' '}
                  <button className="link" disabled={send.isPending} onClick={() => send.mutate(h)}>
                    {t('Send test')}
                  </button>{' '}
                  <button className="link" onClick={() => setLogFor(h)}>
                    {t('Log')}
                  </button>{' '}
                  <button className="link danger" onClick={() => confirm(t('Delete the webhook "{name}"?', { name: h.name })) && remove.mutate(h.id)}>
                    {t('Delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {logFor && <DeliveryLog hook={logFor} onClose={() => setLogFor(null)} />}
    </Panel>
  );
}

function WebhookForm({ hook, onDone }: { hook: Partial<Hook>; onDone: () => void }) {
  const { meta } = useMeta();
  const [f, setF] = useState({
    name: hook.name ?? '',
    url: hook.url ?? '',
    events: hook.events ?? [],
    project_ids: hook.project_ids ?? null,
    headers: headersToText(hook.headers ?? {}),
    payload_template: hook.payload_template ?? '',
    enabled: hook.enabled ?? true,
  });
  const [preview, setPreview] = useState<string | null>(null);
  const [saved, setSaved] = useState<Hook | null>(null);
  const body = () => ({
    name: f.name.trim(),
    url: f.url.trim(),
    events: f.events,
    project_ids: f.project_ids,
    headers: textToHeaders(f.headers),
    payload_template: f.payload_template.trim() || null,
    enabled: f.enabled,
  });
  const save = useMutation({
    mutationFn: () => (hook.id ? api.patch<Hook>(`/api/manage/webhooks/${hook.id}`, body()) : api.post<Hook>('/api/manage/webhooks', body())),
    onSuccess: (h) => (hook.id ? onDone() : setSaved(h)),
  });
  const newSecret = useMutation({
    mutationFn: () => api.patch<Hook>(`/api/manage/webhooks/${hook.id}`, { new_secret: true }),
    onSuccess: (h) => setSaved(h),
  });
  const showPreview = useMutation({
    mutationFn: () => api.post<{ body: unknown }>('/api/manage/webhooks/preview', { payload_template: f.payload_template.trim() || null }),
    onSuccess: (r) => setPreview(JSON.stringify(r.body, null, 2)),
  });

  if (saved) {
    return (
      <div className="subform">
        <div className="banner success">
          {t('Webhook "{name}" saved. Its signing secret (for checking X-BugTracker-Signature):', { name: saved.name })} <CopyLink text={saved.secret} />
        </div>
        <div className="buttons">
          <button className="primary" onClick={onDone}>
            {t('Done')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="subform"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3>{hook.id ? t('Edit webhook') : t('New webhook')}</h3>
      <div className="form-grid">
        <label>
          {t('Name')}
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('e.g. Ship')} required />
        </label>
        <label>
          {t('API endpoint (URL)')}
          <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://ship.example.com/api/bugtracker" required />
        </label>
      </div>
      <div className="form-grid">
        <fieldset className="plain">
          <legend>{t('Events')}</legend>
          {EVENTS.map(([ev, label]) => (
            <label key={ev} className="check">
              <input
                type="checkbox"
                checked={f.events.includes(ev)}
                onChange={(e) => setF({ ...f, events: e.target.checked ? [...f.events, ev] : f.events.filter((x) => x !== ev) })}
              />{' '}
              {t(label)} <span className="subtle mono small">{ev}</span>
            </label>
          ))}
        </fieldset>
        <label>
          {t('Projects')}
          <select
            multiple
            size={Math.min(6, meta.projects.length + 1)}
            value={f.project_ids?.map(String) ?? ['all']}
            onChange={(e) => {
              const v = [...e.target.selectedOptions].map((o) => o.value);
              setF({ ...f, project_ids: v.includes('all') || v.length === 0 ? null : v.map(Number) });
            }}
          >
            <option value="all">{t('All projects')}</option>
            {meta.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block">
        {t('Extra headers (one per line, e.g. Authorization: Bearer <key of the other system>)')}
        <textarea className="mono" rows={3} value={f.headers} onChange={(e) => setF({ ...f, headers: e.target.value })} placeholder="Authorization: Bearer ..." />
      </label>
      <label className="block">
        <span>
          {t('JSON payload')} <span className="subtle">{t('(empty = standard payload with the whole issue)')}</span>
        </span>
        <textarea className="mono" rows={12} value={f.payload_template} onChange={(e) => setF({ ...f, payload_template: e.target.value })} />
      </label>
      <p className="muted small">
        {t('Placeholders: {{event}}, {{issue.id}}, {{issue.summary}}, {{issue.status}}, {{issue.project.name}}, {{issue.handler.username}}, {{issue.url}}, {{actor.username}}, {{note.text}}. {{name|json}} inserts a value as JSON, e.g. {{issue|json}} for the whole issue.')}
      </p>
      <div className="buttons">
        <button type="button" onClick={() => setF({ ...f, payload_template: EXAMPLE_TEMPLATE })}>
          {t('Insert example')}
        </button>
        <button type="button" disabled={showPreview.isPending} onClick={() => showPreview.mutate()}>
          {t('Preview with the latest issue')}
        </button>
        <label className="check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> {t('Enabled')}
        </label>
      </div>
      <ErrorBox error={showPreview.error} />
      {preview && <pre className="json-preview">{preview}</pre>}
      {hook.id && (
        <p className="small">
          {t('Signing secret:')} <CopyLink text={hook.secret ?? ''} />{' '}
          <button type="button" className="link" onClick={() => confirm(t('Create a new secret? The receiver must be updated.')) && newSecret.mutate()}>
            {t('New secret')}
          </button>
        </p>
      )}
      <ErrorBox error={save.error || newSecret.error} />
      <div className="buttons">
        <button className="primary" disabled={save.isPending || !f.name.trim() || !f.url.trim() || f.events.length === 0}>
          {t('Save')}
        </button>
        <button type="button" onClick={onDone}>
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}

function DeliveryLog({ hook, onClose }: { hook: Hook; onClose: () => void }) {
  const { data, error } = useQuery({ queryKey: ['webhook-log', hook.id], queryFn: () => api.get<DeliveryRow[]>(`/api/manage/webhooks/${hook.id}/deliveries`) });
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Modal title={t('Deliveries of "{name}"', { name: hook.name })} onClose={onClose} width={900}>
      <ErrorBox error={error} />
      {data?.length === 0 && <p className="muted">{t('Nothing was sent yet.')}</p>}
      {data && data.length > 0 && (
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Time')}</th>
              <th>{t('Event')}</th>
              <th>{t('Issue')}</th>
              <th>{t('Result')}</th>
              <th className="num">ms</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <Fragment key={d.id}>
                <tr className="clickable" onClick={() => setOpen(open === d.id ? null : d.id)}>
                  <td className="nowrap">{fmtDate(d.created_at)}</td>
                  <td className="mono small">{d.event}</td>
                  <td>{d.issue_id ? `#${d.issue_id}` : ''}</td>
                  <td className={d.error ? 'error' : ''}>{d.error ?? `HTTP ${d.status}`}</td>
                  <td className="num">{d.duration_ms}</td>
                </tr>
                {open === d.id && (
                  <tr>
                    <td colSpan={5}>
                      <div className="small subtle">{t('Request')}</div>
                      <pre className="json-preview">{d.request}</pre>
                      <div className="small subtle">{t('Response')}</div>
                      <pre className="json-preview">{d.response || '—'}</pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function ApiGuide() {
  const base = `${location.origin}/api/v1`;
  const example = `curl -H "Authorization: Bearer <key>" "${base}/issues?project=WikiB&status=new,assigned&per_page=50"

curl -X POST -H "Authorization: Bearer <key>" -H "Content-Type: application/json" \\
  -d '{"project": "WikiB", "summary": "Crash on start", "description": "…", "priority": "high", "handler": "kav", "tags": ["ship"]}' \\
  "${base}/issues"

curl -X PATCH -H "Authorization: Bearer <key>" -H "Content-Type: application/json" \\
  -d '{"status": "resolved", "resolution": "fixed", "note": "Fixed in 3.10.2"}' \\
  "${base}/issues/22593"`;
  const endpoints: [string, string][] = [
    ['GET /me', N_('the key: team, user, read-only')],
    ['GET /enums', N_('valid values for status, priority, severity, resolution …')],
    ['GET /projects', N_('projects of the team')],
    ['GET /projects/{id}', N_('categories, versions and custom fields of a project')],
    ['GET /users', N_('members of the team')],
    ['GET /issues', N_('issues; filters: project, status, priority, severity, handler, reporter, category, target_version, search, updated_since, include_closed, page, per_page (max 500), include=notes')],
    ['GET /issues/{id}', N_('one issue with its notes')],
    ['POST /issues', N_('create an issue (JSON)')],
    ['PATCH /issues/{id}', N_('change an issue; "note" adds a note at the same time')],
    ['POST /issues/{id}/notes', N_('add a note: {"text": "...", "private": false}')],
  ];
  return (
    <Panel title={t('REST API')}>
      <p>
        {t('Base URL:')} <code>{base}</code> · {t('send the key as')} <code>Authorization: Bearer &lt;key&gt;</code> {t('or')} <code>X-Api-Key: &lt;key&gt;</code>
      </p>
      <table className="simple">
        <tbody>
          {endpoints.map(([e, d]) => (
            <tr key={e}>
              <td className="mono nowrap">{e}</td>
              <td className="small">{t(d)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">
        {t('Values like status or priority can be given as names ("high") or Mantis numbers (40); projects, categories and people by name or id. The SOAP API for Mantis tools stays available at /api/soap/mantisconnect.php.')}
      </p>
      <pre className="json-preview">{example}</pre>
    </Panel>
  );
}
